import { useState } from "react";
import { ArrowUpRight, SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { SORTS, filterVehicles, gbp, makes, sortVehicles, stockUpdatedLabel, vehicles } from "@/lib/inventory";
import { useTitle } from "@/lib/site";
import { DEALER } from "@/data/dealer";
import { useStockFilters } from "@/hooks/useStockFilters";
import { PageHeader } from "@/components/site/Shared";
import { Reveal } from "@/components/motion/Reveal";
import { FilterPanel } from "@/components/stock/FilterPanel";
import { ActiveChips, NoResults } from "@/components/stock/StockParts";
import { VehicleCard } from "@/components/vehicles/VehicleCard";

const StockStats = () => {
  const prices = vehicles.map((v) => v.price);
  const stats = [
    [vehicles.length, "Cars listed"],
    [gbp(Math.min(...prices)), "Cheapest car"],
    [makes.length, "Makes in stock"],
  ];
  return (
    <dl className="grid grid-cols-3 gap-px overflow-hidden rounded-lg border border-line bg-line" data-testid="stock-stats">
      {stats.map(([value, label]) => (
        <div key={label} className="flex flex-col-reverse bg-white p-4 sm:p-6">
          <dt className="mt-1 text-xs text-steel sm:text-sm">{label}</dt>
          <dd className="font-display text-xl font-extrabold sm:text-3xl">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

export default function UsedCars() {
  useTitle("Browse Our Used Car Stock | YK Motors Peterborough");
  const stock = useStockFilters();
  const { filters, update, activeCount } = stock;
  const [drawer, setDrawer] = useState(false);
  const results = sortVehicles(filterVehicles(vehicles, filters), filters.sort);

  return (
    <>
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Used cars" }]}
        eyebrow="Used cars"
        title={["Used cars for sale", "in Peterborough."]}
        intro={`Browse ${vehicles.length} used cars from YK Motors. Stock comes straight from our AutoTrader adverts and refreshes every hour (${stockUpdatedLabel()}) — please call to confirm availability.`}
        aside={<StockStats />}
      >
        <a
          href={DEALER.autotrader.stock}
          target="_blank"
          rel="noopener noreferrer"
          className="link-underline mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-ink"
          data-testid="stock-autotrader-link"
        >
          View live stock on AutoTrader <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </a>
      </PageHeader>

      <section className="container-x py-10 lg:py-14" aria-label="Stock results">
        <div className="lg:grid lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-10 xl:gap-12">
          <aside className="hidden lg:block" aria-label="Filters">
            <div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pb-6 pr-1" data-lenis-prevent>
              <FilterPanel {...stock} prefix="desktop" />
            </div>
          </aside>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line pb-5">
              <p aria-live="polite" className="text-sm text-steel" data-testid="results-count">
                <span className="mr-1.5 font-display text-2xl font-extrabold tabular-nums text-ink">{results.length}</span>
                {results.length === 1 ? "car" : "cars"} {activeCount ? `match${results.length === 1 ? "es" : ""} your filters` : "in stock"}
              </p>
              <div className="flex items-center gap-2 sm:gap-3">
                <button type="button" onClick={() => setDrawer(true)} className="btn btn-outline btn-sm lg:hidden" data-testid="open-filters-button">
                  <SlidersHorizontal aria-hidden="true" /> Filters{activeCount ? ` (${activeCount})` : ""}
                </button>
                <label htmlFor="sort-select" className="hidden text-sm text-steel sm:block">
                  Sort by
                </label>
                <select
                  id="sort-select"
                  aria-label="Sort cars"
                  value={filters.sort}
                  onChange={(e) => update({ sort: e.target.value })}
                  className="field h-10 w-auto text-sm"
                  data-testid="sort-select"
                >
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <ActiveChips {...stock} />

            <div className="mt-8">
              {results.length ? (
                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="stock-grid">
                  {results.map((v, i) => (
                    <Reveal key={v.id} delay={(i % 3) * 0.06} y={20}>
                      <VehicleCard v={v} testId={`stock-card-${v.id}`} />
                    </Reveal>
                  ))}
                </div>
              ) : (
                <NoResults clear={stock.clear} />
              )}
            </div>
          </div>
        </div>
      </section>

      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="left" aria-describedby={undefined} className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="filters-drawer">
          <SheetTitle className="sr-only">Filter cars</SheetTitle>
          <div className="flex-1 overflow-y-auto px-5 pb-6 pt-14" data-lenis-prevent>
            <FilterPanel {...stock} prefix="mobile" />
          </div>
          <div className="border-t border-line bg-white p-4">
            <button type="button" onClick={() => setDrawer(false)} className="btn btn-primary w-full" data-testid="filters-drawer-show-results-button">
              Show {results.length} {results.length === 1 ? "car" : "cars"}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
